import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { LEAVE_TYPES } from "@/constants/roles";
import { API_BASE } from "@/lib/api";
import { ApprovalChain } from "@/pages/LeavePage";

export default function ApprovalsPage() {
  const [leaves, setLeaves] = useState([]);
  const [overtimes, setOvertimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("leave");

  const load = async () => {
    setLoading(true);
    try {
      const [l, o] = await Promise.all([
        api.get("/leave/pending-approvals"),
        api.get("/overtime/pending-approvals"),
      ]);
      setLeaves(l.data); setOvertimes(o.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (kind, id, action) => {
    const note = window.prompt(`Catatan untuk ${action === "approve" ? "persetujuan" : "penolakan"} (opsional):`, "") || "";
    try {
      await api.post(`/${kind}/${id}/${action}`, { note });
      toast.success(action === "approve" ? "Disetujui." : "Ditolak.");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal.");
    }
  };

  const totalPending = leaves.length + overtimes.length;

  return (
    <div data-testid={TESTIDS.approvalsPage} className="space-y-6">
      <div>
        <div className="label-tiny">Persetujuan Menunggu</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">
          Menunggu Persetujuan Anda
          {totalPending > 0 && <span className="ml-3 inline-flex items-center bg-signal-late text-white text-lg px-3 py-1 align-middle">{totalPending}</span>}
        </h1>
      </div>

      <div className="flex border-b border-black/10">
        {["leave", "overtime"].map((t) => {
          const count = t === "leave" ? leaves.length : overtimes.length;
          return (
            <button key={t} onClick={() => setTab(t)}
              className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === t ? "border-klein text-klein" : "border-transparent text-gray-500 hover:text-black"}`}>
              {t === "leave" ? "Cuti" : "Lembur"} {count > 0 && <span className="ml-1 bg-signal-late text-white text-xs px-1.5">{count}</span>}
            </button>
          );
        })}
      </div>

      {loading ? <div className="text-gray-500">Memuat...</div> : (
        tab === "leave" ? (
          <RequestList
            kind="leave"
            items={leaves}
            onAct={act}
            emptyMessage="Tidak ada pengajuan cuti yang menunggu persetujuan Anda."
            renderMeta={(r) => (
              <>
                <MetaItem label="Jenis" value={LEAVE_TYPES[r.leave_type] || r.leave_type} />
                <MetaItem label="Periode" value={`${r.start_date} → ${r.end_date}`} />
                <MetaItem label="Hari" value={r.days} />
                {r.attachment_path && (
                  <MetaItem label="Attachment" value={
                    <a href={`${API_BASE}/leave/${r.request_id}/attachment?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
                      target="_blank" rel="noreferrer"
                      className="text-klein hover:underline">{r.attachment_filename || "Lihat"}</a>
                  } />
                )}
              </>
            )}
          />
        ) : (
          <RequestList
            kind="overtime"
            items={overtimes}
            onAct={act}
            emptyMessage="Tidak ada pengajuan lembur yang menunggu persetujuan Anda."
            renderMeta={(r) => (
              <>
                <MetaItem label="Tanggal" value={r.date} />
                <MetaItem label="Jam" value={`${r.start_time} – ${r.end_time}`} />
              </>
            )}
          />
        )
      )}
    </div>
  );
}

function RequestList({ kind, items, onAct, emptyMessage, renderMeta }) {
  if (items.length === 0) return <div className="border border-black/10 bg-white p-8 text-center text-gray-500">{emptyMessage}</div>;
  return (
    <div className="space-y-4">
      {items.map((r) => (
        <div key={r.request_id} className="border border-black/10 bg-white p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-2 mb-2">
                <div className="font-display text-xl font-bold">{r.user_name}</div>
                <span className="text-xs text-gray-500">({r.user_email})</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                {renderMeta(r)}
              </div>
              <div className="mt-4">
                <div className="label-tiny mb-2">Alasan</div>
                <div className="text-sm text-gray-700">{r.reason}</div>
              </div>
              <div className="mt-4">
                <div className="label-tiny mb-2">Rantai Approval</div>
                <ApprovalChain approvers={r.approvers} currentStep={r.current_step} />
              </div>
            </div>
            <div className="flex flex-col gap-2 min-w-[140px]">
              <button data-testid={`${TESTIDS.approveBtn}-${r.request_id}`} onClick={() => onAct(kind, r.request_id, "approve")}
                className="inline-flex items-center justify-center gap-2 bg-signal-ok text-white px-4 py-2 hover:bg-black transition-colors text-sm font-semibold">
                <Check className="w-4 h-4" /> Setujui
              </button>
              <button data-testid={`${TESTIDS.rejectBtn}-${r.request_id}`} onClick={() => onAct(kind, r.request_id, "reject")}
                className="inline-flex items-center justify-center gap-2 bg-signal-late text-white px-4 py-2 hover:bg-black transition-colors text-sm font-semibold">
                <X className="w-4 h-4" /> Tolak
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MetaItem({ label, value }) {
  return (
    <div>
      <div className="label-tiny">{label}</div>
      <div className="font-semibold text-sm mt-0.5">{value}</div>
    </div>
  );
}
